/**
 * AuthGate — Axe 3 audit S++ (auth toggleable, dev-friendly).
 *
 * Comportement :
 *   1. Au mount, fetch GET /api/auth/status.
 *   2. Si {enabled: false} → render `children` direct, transparent.
 *   3. Si {enabled: true} :
 *        - Si un token existe en localStorage et que GET /api/health passe (200),
 *          on considère l'utilisateur authentifié → render `children`.
 *        - Sinon → afficher `<LoginCard />` (input token + bouton désactiver
 *          l'auth si dev_toggle_allowed).
 *
 * Aucune redirection : on remplace juste le contenu de `<main>` par la
 * carte login. Le routing reste intact.
 */

import { useEffect, useState } from 'react';
import { api, ApiError, authToken } from '@/lib/api';
import { Lab } from '@/lib/toast';

interface AuthStatus {
  enabled: boolean;
  token_configured: boolean;
  env_enabled: boolean;
  dev_toggle_allowed: boolean;
  mode: 'dev' | 'prod';
}

type GateState =
  | { kind: 'loading' }
  | { kind: 'open' }                             // auth disabled OR auth-OK
  | { kind: 'login'; status: AuthStatus }
  | { kind: 'error'; message: string };

/** Probe the backend with a protected-ish endpoint. /api/systems works
 * (it returns 200 if no auth, or 401/403 if auth is required and token KO). */
async function probeAuth(): Promise<boolean> {
  try {
    await api.systems();
    return true;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return false;
    }
    // Other errors (500, network…) — treat as "open" so the user isn't locked
    // out by an unrelated backend issue (the BackendStatusBanner will show it).
    return true;
  }
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await api.authStatus();
        if (!status.enabled) {
          if (!cancelled) setState({ kind: 'open' });
          return;
        }
        const ok = await probeAuth();
        if (cancelled) return;
        setState(ok ? { kind: 'open' } : { kind: 'login', status });
      } catch (err) {
        if (cancelled) return;
        // /api/auth/status doit toujours marcher (pas d'auth dessus). Si KO,
        // on suppose le backend down → on laisse passer pour que l'utilisateur
        // voie le BackendStatusBanner plutôt qu'une page login orpheline.
        setState({ kind: 'open' });
        // eslint-disable-next-line no-console
        console.warn('AuthGate: auth status fetch failed, assuming open', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return (
      <div style={{ padding: 64, textAlign: 'center', color: 'var(--text-muted)' }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.1em' }}>
          VÉRIFICATION DE L'AUTHENTIFICATION…
        </div>
      </div>
    );
  }

  if (state.kind === 'login') {
    return <LoginCard status={state.status} onSuccess={() => setState({ kind: 'open' })} />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// LoginCard — saisie du token + option « désactiver l'auth pour cette session »
// ---------------------------------------------------------------------------

function LoginCard({
  status,
  onSuccess,
}: {
  status: AuthStatus;
  onSuccess: () => void;
}) {
  const [token, setToken] = useState<string>(authToken.get());
  const [busy, setBusy] = useState<boolean>(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      await Lab.alert('Le token est obligatoire.', 'Champ manquant');
      return;
    }
    setBusy(true);
    try {
      authToken.set(token.trim());
      // Probe avec le nouveau token
      try {
        await api.systems();
        Lab.toast('Authentification réussie.', 'pass');
        onSuccess();
      } catch (err) {
        authToken.clear();
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          await Lab.alert('Token invalide. Vérifie la valeur de CEE_DASHBOARD_TOKEN côté serveur.', 'Authentification refusée');
        } else {
          await Lab.alert("Le backend n'a pas répondu correctement. Réessaye plus tard.", 'Erreur réseau');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisableAuth = async () => {
    const ok = await Lab.confirm(
      "Désactiver complètement l'authentification pour ce serveur ?\n\nÀ utiliser uniquement en phase de développement. Toute personne sur le réseau pourra accéder à l'API.",
      "Désactiver l'authentification",
      { danger: true },
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.authToggle(false);
      if (!res.enabled) {
        Lab.toast("Authentification désactivée pour cette session serveur.", 'warn');
        onSuccess();
      }
    } catch (err) {
      const detail = err instanceof ApiError ? String(err.body ?? err.status) : String(err);
      await Lab.alert("Impossible de désactiver l'auth : " + detail, 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 80px)',
        display: 'grid',
        placeItems: 'center',
        padding: 32,
      }}
    >
      <form
        onSubmit={handleLogin}
        style={{
          width: 'min(420px, 100%)',
          background: 'var(--surface, #fff)',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 'var(--radius-lg, 8px)',
          padding: 32,
          display: 'grid',
          gap: 16,
        }}
      >
        <header style={{ display: 'grid', gap: 4 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
            Authentification requise
          </h1>
          <p style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', margin: 0 }}>
            Entre le token bearer du serveur (variable d'env <code>CEE_DASHBOARD_TOKEN</code>).
          </p>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.05em', color: 'var(--text-muted, #6b7280)', marginTop: 4 }}>
            MODE : {status.mode.toUpperCase()} · ENV : {status.env_enabled ? 'auth=ON' : 'auth=off'}
            {status.dev_toggle_allowed && ' · TOGGLE DEV AUTORISÉ'}
          </div>
        </header>

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Token</span>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoFocus
            autoComplete="current-password"
            disabled={busy}
            placeholder="Saisis ton token…"
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 'var(--radius-sm, 4px)',
              fontSize: 14,
              fontFamily: 'var(--font-mono, monospace)',
            }}
          />
        </label>

        <button
          type="submit"
          disabled={busy || !token.trim()}
          style={{
            padding: '10px 16px',
            background: 'var(--accent, #2563eb)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-sm, 4px)',
            fontSize: 14,
            fontWeight: 500,
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy || !token.trim() ? 0.6 : 1,
          }}
        >
          {busy ? 'Vérification…' : 'Se connecter'}
        </button>

        {status.dev_toggle_allowed && (
          <>
            <div style={{ height: 1, background: 'var(--border, #e5e7eb)' }} />
            <div style={{ display: 'grid', gap: 8 }}>
              <p style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', margin: 0 }}>
                <strong>Mode développement</strong> — tu peux désactiver l'authentification
                sur ce serveur sans redémarrer. Cette option n'est pas disponible en production
                (variable d'env <code>CEE_AUTH_DEV_TOGGLE=0</code>).
              </p>
              <button
                type="button"
                onClick={handleDisableAuth}
                disabled={busy}
                style={{
                  padding: '8px 12px',
                  background: 'transparent',
                  color: 'var(--danger, #b91c1c)',
                  border: '1px solid var(--danger, #b91c1c)',
                  borderRadius: 'var(--radius-sm, 4px)',
                  fontSize: 12,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                Désactiver l'authentification pour cette session
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
